import { useCallback, useEffect, useId, useRef, useState, type ReactNode, type UIEvent } from "react"
import { createPortal } from "react-dom"
import { ChevronDown } from "lucide-react"
import { RemoveScroll } from "react-remove-scroll"
import { ActionHintTooltip } from "@/components/ui/action-hint-tooltip"
import { useDropdownMenu, useFitContentWidth } from "@/components/ui/dropdown-menu-position"
import { cn } from "@/lib/utils"

export type SearchableComboboxOption = {
  value: string
  label: string
  group?: string
  disabled?: boolean
  /** Тултип на строке, когда пункт нельзя выбрать. */
  disabledHint?: string
  /** Миниатюра слева от подписи. */
  thumbUrl?: string | null
  /** Аватар или другой узел слева от подписи. Если задан, картинка thumbUrl не рисуется. */
  leading?: ReactNode
}

/** sm — тулбары и фильтры; md — поле формы, как Input. */
export type SearchableComboboxSize = "sm" | "md"

type SearchableComboboxProps = {
  value: string
  onChange: (value: string) => void
  options: SearchableComboboxOption[]
  placeholder?: string
  disabled?: boolean
  className?: string
  inputClassName?: string
  /** Where the list opens relative to the input. */
  dropdownDirection?: "up" | "down"
  /** Дропдаун подстраивает ширину под самый длинный пункт (минимум — ширина инпута). */
  dropdownAutoWidth?: boolean
  /** Portal to document.body so the list can extend past dialog edges. */
  portalToBody?: boolean
  /** Разрешить свободный ввод (value может не совпадать с option.value). */
  allowCustom?: boolean
  ariaLabel?: string
  /** sm: h-8 text-xs. md: h-10 text-sm, как Input. */
  size?: SearchableComboboxSize
  /**
   * Список уже отфильтрован снаружи. Поле только сообщает текст и конец прокрутки.
   * Закрытая подпись берётся из selectedOption, а не из текущей страницы options.
   */
  filterLocally?: boolean
  onQueryChange?: (query: string) => void
  onReachEnd?: () => void
  loading?: boolean
  emptyLabel?: string
  selectedOption?: SearchableComboboxOption | null
}

/** pl + pr-8 (шеврон) + border. sm: pl-2, md: pl-3. */
const FIT_CONTENT_CHROME_PX: Record<SearchableComboboxSize, number> = {
  sm: 8 + 32 + 2,
  md: 12 + 32 + 2,
}

function groupOptions(options: SearchableComboboxOption[]) {
  const groups = new Map<string, SearchableComboboxOption[]>()
  for (const o of options) {
    const key = o.group ?? ""
    const list = groups.get(key) ?? []
    list.push(o)
    groups.set(key, list)
  }
  return [...groups.entries()].map(([group, items]) => ({ group, items }))
}

function firstNavigableIndex(options: SearchableComboboxOption[]) {
  const index = options.findIndex((o) => !o.disabled)
  return index >= 0 ? index : 0
}

function lastNavigableIndex(options: SearchableComboboxOption[]) {
  for (let i = options.length - 1; i >= 0; i -= 1) {
    if (!options[i]?.disabled) return i
  }
  return Math.max(options.length - 1, 0)
}

function OptionLabel({ option }: { option: SearchableComboboxOption }) {
  const label = (
    <span className="flex w-full items-center gap-2 px-2 py-1">
      {option.leading ? (
        <span className="flex size-6 shrink-0 items-center justify-center">{option.leading}</span>
      ) : option.thumbUrl ? (
        <img src={option.thumbUrl} alt="" className="h-5 w-5 shrink-0 rounded-md object-cover" />
      ) : null}
      <span className="min-w-0 flex-1 truncate">{option.label}</span>
    </span>
  )
  if (!option.disabled || !option.disabledHint) return label

  return (
    <ActionHintTooltip label={option.disabledHint} side="right" className="block w-full">
      {label}
    </ActionHintTooltip>
  )
}

export function SearchableCombobox({
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  className,
  inputClassName,
  dropdownDirection = "down",
  dropdownAutoWidth = false,
  portalToBody = false,
  allowCustom = false,
  ariaLabel,
  size = "sm",
  filterLocally = true,
  onQueryChange,
  onReachEnd,
  loading = false,
  emptyLabel,
  selectedOption,
}: SearchableComboboxProps) {
  const listboxId = useId()
  const [open, setOpen] = useState(false)
  const [filter, setFilter] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLUListElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const optionRefs = useRef<Array<HTMLLIElement | null>>([])
  const shouldScrollActiveOptionRef = useRef(false)

  const selected = selectedOption ?? options.find((o) => o.value === value)
  const displayValue = open ? filter : (selected?.label ?? (allowCustom ? value : ""))
  const showSelectedLeading = !open && selected?.leading != null
  const showSelectedThumb = !open && !showSelectedLeading && Boolean(selected?.thumbUrl)

  const dismissDropdown = useCallback(() => {
    setOpen(false)
    setFilter("")
  }, [])

  const filtered =
    filterLocally && filter
      ? options.filter(
          (o) =>
            o.label.toLowerCase().includes(filter.toLowerCase()) ||
            o.value.toLowerCase().includes(filter.toLowerCase()),
        )
      : options

  const grouped = groupOptions(filtered)
  const hasGroups = options.some((o) => o.group)
  const activeOption = filtered[activeIndex]
  const activeOptionId = activeOption ? `${listboxId}-${activeOption.value}` : undefined

  const fitWidthPx = useFitContentWidth({
    enabled: dropdownAutoWidth,
    labels: options.map((o) => o.label),
    chromePx: FIT_CONTENT_CHROME_PX[size],
    anchorRef: inputRef,
    probeRef: measureRef,
  })

  const menuOpen = open && (filtered.length > 0 || !filterLocally)
  const { menuStyle, portalRoot } = useDropdownMenu({
    open: menuOpen,
    disabled,
    direction: dropdownDirection,
    align: "left",
    anchorRef: inputRef,
    menuRef,
    rootRef,
    portalToBody,
    positionKey: `${dropdownAutoWidth}:${fitWidthPx}:${filter}`,
    resolveWidth: (rect) => {
      if (!dropdownAutoWidth) return { minWidth: rect.width }

      return {
        minWidth: Math.max(rect.width, fitWidthPx ?? 0),
        width: "max-content",
        maxWidth: "min(90vw, 32rem)",
      }
    },
    onOutsideClick: dismissDropdown,
  })

  const selectOption = useCallback(
    (option: SearchableComboboxOption) => {
      if (option.disabled) return
      onChange(option.value)
      setFilter("")
      setOpen(false)
    },
    [onChange],
  )

  const moveActiveIndex = useCallback(
    (delta: number) => {
      if (filtered.length === 0) return

      shouldScrollActiveOptionRef.current = true
      setActiveIndex((current) => {
        if (filtered.length === 1) return filtered[0]?.disabled ? current : 0

        let next = current
        for (let step = 0; step < filtered.length; step += 1) {
          next = (next + delta + filtered.length) % filtered.length
          if (!filtered[next]?.disabled) return next
        }
        return current
      })
    },
    [filtered],
  )

  const setActiveIndexFromKeyboard = useCallback((index: number) => {
    shouldScrollActiveOptionRef.current = true
    setActiveIndex(index)
  }, [])

  const setActiveIndexFromPointer = useCallback((index: number) => {
    shouldScrollActiveOptionRef.current = false
    setActiveIndex((current) => (current === index ? current : index))
  }, [])

  const commitCustom = (text: string) => {
    if (!allowCustom) return
    onChange(text.trim())
    setFilter("")
    setOpen(false)
  }

  const shouldCommitCustomOnBlur = () => {
    if (!allowCustom) return false
    const trimmed = filter.trim()
    if (!trimmed) return false
    if (selected && selected.label.toLowerCase().startsWith(trimmed.toLowerCase())) return false
    if (trimmed === (selected?.label ?? value)) return false
    return true
  }

  useEffect(() => {
    if (!open || !onQueryChange) return
    onQueryChange(filter)
  }, [filter, onQueryChange, open])

  useEffect(() => {
    if (!open || filterLocally) return
    shouldScrollActiveOptionRef.current = false
    setActiveIndex(0)
  }, [filter, filterLocally, open])

  useEffect(() => {
    if (!open || !filterLocally) return

    const selectedIndex = filtered.findIndex((o) => o.value === value)
    shouldScrollActiveOptionRef.current = false
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : firstNavigableIndex(filtered))
  }, [filterLocally, filtered, open, value])

  const handleMenuScroll = (event: UIEvent<HTMLUListElement>) => {
    if (!onReachEnd) return
    const node = event.currentTarget
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 24) onReachEnd()
  }

  useEffect(() => {
    if (!open) return
    if (!shouldScrollActiveOptionRef.current) return

    shouldScrollActiveOptionRef.current = false
    optionRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" })
  }, [activeIndex, open])

  const renderOption = (option: SearchableComboboxOption, flatIndex: number) => {
    const highlighted = flatIndex === activeIndex
    const isSelected = option.value === value

    return (
      <li
        key={option.value}
        id={`${listboxId}-${option.value}`}
        ref={(node) => {
          optionRefs.current[flatIndex] = node
        }}
        role="option"
        aria-selected={isSelected}
        onPointerMove={() => setActiveIndexFromPointer(flatIndex)}
        onMouseDown={(e) => {
          e.preventDefault()
          selectOption(option)
        }}
        className={cn(
          "cursor-pointer text-xs transition-colors hover:bg-ui-accent/25",
          isSelected ? "bg-ui-accent-soft text-ui-accent-text" : "text-ui-text-primary",
          highlighted && !isSelected && "bg-ui-accent/15",
          option.disabled && "cursor-not-allowed opacity-40",
          dropdownAutoWidth && "whitespace-nowrap",
        )}
      >
        <OptionLabel option={option} />
      </li>
    )
  }

  let runningIndex = 0

  const menu =
    open && !disabled && menuOpen && menuStyle && portalRoot ? (
      <ul
        ref={menuRef}
        id={listboxId}
        role="listbox"
        data-panel-wheel-isolate
        style={menuStyle}
        className="max-h-60 overflow-auto rounded-lg border border-ui-border bg-ui-bg-elevated py-1 shadow-xl shadow-black/40"
        onScroll={handleMenuScroll}
      >
        {filtered.length === 0 ? (
          <li className="px-3 py-2 text-xs text-ui-text-muted">{loading ? "…" : emptyLabel}</li>
        ) : hasGroups ? (
          grouped.map(({ group, items }) => (
            <li key={group || "__ungrouped"}>
              {group ? (
                <div className="px-3 py-1.5 text-[10px] font-semibold tracking-wide text-ui-text-muted uppercase">
                  {group}
                </div>
              ) : null}
              <ul>{items.map((o) => renderOption(o, runningIndex++))}</ul>
            </li>
          ))
        ) : (
          filtered.map((o) => renderOption(o, runningIndex++))
        )}
      </ul>
    ) : null

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      {dropdownAutoWidth ? (
        <span
          ref={measureRef}
          aria-hidden
          className={cn(
            "pointer-events-none invisible absolute top-0 left-0 whitespace-nowrap",
            size === "md" ? "text-sm" : "text-xs leading-none",
          )}
        />
      ) : null}
      {showSelectedLeading ? (
        <span className="pointer-events-none absolute top-1/2 left-1 flex size-6 -translate-y-1/2 items-center justify-center">
          {selected?.leading}
        </span>
      ) : showSelectedThumb ? (
        <img
          src={selected?.thumbUrl ?? ""}
          alt=""
          className="pointer-events-none absolute top-1/2 left-1.5 h-5 w-5 -translate-y-1/2 rounded-md object-cover"
        />
      ) : null}
      <input
        ref={inputRef}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={open ? activeOptionId : undefined}
        aria-autocomplete="list"
        className={cn(
          "flex w-full rounded-lg border border-ui-border bg-ui-surface-sunken py-0 pr-8 text-ui-text-primary outline-none placeholder:text-ui-text-muted focus-visible:ring-2 focus-visible:ring-[var(--ui-focus-ring)]",
          size === "md" ? "h-10 pl-3 text-sm" : "h-8 pl-2 text-xs leading-none",
          showSelectedLeading && "pl-9",
          showSelectedThumb && "pl-8",
          inputClassName,
        )}
        value={displayValue}
        disabled={disabled}
        onFocus={() => {
          if (disabled) return
          setOpen(true)
          setFilter("")
        }}
        onClick={() => {
          if (disabled) return
          setOpen(true)
          setFilter("")
        }}
        onChange={(e) => {
          if (disabled) return
          setFilter(e.target.value)
          setOpen(true)
        }}
        onBlur={() => {
          if (disabled) return
          if (shouldCommitCustomOnBlur()) {
            commitCustom(filter)
          } else {
            dismissDropdown()
          }
        }}
        onKeyDown={(e) => {
          switch (e.key) {
            case "ArrowDown":
              e.preventDefault()
              if (!open) setOpen(true)
              moveActiveIndex(1)
              break
            case "ArrowUp":
              e.preventDefault()
              if (!open) setOpen(true)
              moveActiveIndex(-1)
              break
            case "Home":
              e.preventDefault()
              if (!open) setOpen(true)
              setActiveIndexFromKeyboard(firstNavigableIndex(filtered))
              break
            case "End":
              e.preventDefault()
              if (!open) setOpen(true)
              setActiveIndexFromKeyboard(lastNavigableIndex(filtered))
              break
            case "Enter": {
              e.preventDefault()
              const active = filtered[activeIndex]
              if (active && !active.disabled) {
                selectOption(active)
              } else if (allowCustom && filter.trim()) {
                commitCustom(filter)
              }
              break
            }
            case "Escape":
              setOpen(false)
              setFilter("")
              break
          }
        }}
        placeholder={placeholder}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-hidden
        className={cn(
          "absolute inset-y-0 right-0 flex w-8 items-center justify-center rounded-r-lg",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
        )}
        onMouseDown={(e) => {
          e.preventDefault()
          if (disabled) return
          if (open) {
            dismissDropdown()
          } else {
            setOpen(true)
            setFilter("")
            inputRef.current?.focus()
          }
        }}
      >
        <ChevronDown className={cn("h-3.5 w-3.5 text-ui-text-muted transition-transform", open && "rotate-180")} />
      </button>
      {menu && portalRoot
        ? createPortal(
            portalToBody ? (
              <RemoveScroll enabled={open} allowPinchZoom removeScrollBar={false}>
                {menu}
              </RemoveScroll>
            ) : (
              menu
            ),
            portalRoot,
          )
        : null}
    </div>
  )
}
